class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this._bdSearchDrawerClosing = false;
    this._bdReturnFocusEl = null;
    this._bdScrollY = 0;

    this.detailsContainer.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() !== 'ESCAPE') return;
      if (this.isBdNutritionModal && document.querySelector('.bd-nutrition-label-modal[open]')) return;
      this.close(this.isBdNutritionModal ? false : true);
    });
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelectorAll(
      'button[type="button"].search-modal__close-button, button[type="button"].bd-search-modal__backdrop, button[type="button"].bd-nutrition-modal__backdrop'
    ).forEach((btn) =>
      btn.addEventListener('click', (event) => {
        if (this.isBdNutritionModal) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.close(this.isBdNutritionModal ? false : true);
      })
    );

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  get usesBdSearchDrawerAnim() {
    return Boolean(
      this.detailsContainer &&
        this.detailsContainer.querySelector('.bd-search-modal, .bd-nutrition-modal')
    );
  }

  get isBdNutritionModal() {
    return this.classList.contains('bd-ingredients-nutrition-modal');
  }

  lockPageScroll() {
    if (this.isBdNutritionModal) {
      this._bdScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('bd-nutrition-modal-lock');
      return;
    }
    document.body.classList.add('overflow-hidden');
  }

  unlockPageScroll() {
    if (this.isBdNutritionModal) {
      document.documentElement.classList.remove('bd-nutrition-modal-lock');
      document.body.classList.remove('overflow-hidden');
      return;
    }
    document.body.classList.remove('overflow-hidden');
  }

  prepareNutritionModalClose() {
    removeTrapFocus(null);

    const active = document.activeElement;
    if (active && this.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }

    this._bdReturnFocusEl = null;
  }

  restoreNutritionScrollPosition() {
    const y = this._bdScrollY || 0;
    const restore = () => {
      if (Math.abs(window.scrollY - y) > 1) {
        window.scrollTo({ top: y, left: 0, behavior: 'instant' });
      }
    };
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }

  finishNutritionModalClose(details) {
    if (this.summaryToggle) {
      this.summaryToggle.setAttribute('tabindex', '-1');
    }

    details.removeAttribute('open');
    details.classList.remove('bd-search-modal-opening');

    const active = document.activeElement;
    if (active && this.contains(active) && typeof active.blur === 'function') {
      active.blur();
    }

    const sectionHeader = this.closest('.section-header');
    if (sectionHeader) sectionHeader.classList.remove('bd-search-modal-open');
    document.body.removeEventListener('click', this.onBodyClickEvent);

    this.unlockPageScroll();
    this.restoreNutritionScrollPosition();
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (this._ignoreBodyCloseUntil && Date.now() < this._ignoreBodyCloseUntil) return;
    if (event.target.closest('[data-bd-nutrition-trigger]')) return;
    if (this.isBdNutritionModal && event.target.closest('.bd-nutrition-label-modal')) return;

    if (
      !this.contains(event.target) ||
      event.target.classList.contains('modal-overlay') ||
      event.target.classList.contains('bd-search-modal__backdrop') ||
      event.target.classList.contains('bd-nutrition-modal__backdrop')
    )
      this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    const details = this.detailsContainer;
    this._bdSearchDrawerClosing = false;
    details.setAttribute('open', true);

    if (this.usesBdSearchDrawerAnim) {
      details.classList.remove('bd-search-modal-opening');
      const reducedMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        details.classList.add('bd-search-modal-opening');
      } else {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            details.classList.add('bd-search-modal-opening');
          });
        });
      }
    }

    const sectionHeader = this.closest('.section-header');
    if (sectionHeader) sectionHeader.classList.add('bd-search-modal-open');

    this._ignoreBodyCloseUntil = Date.now() + 400;
    this.lockPageScroll();
    window.setTimeout(() => {
      document.body.addEventListener('click', this.onBodyClickEvent);
    }, 0);

    const panel =
      this.detailsContainer.querySelector('.search-modal__content.bd-search-modal__panel') ||
      this.detailsContainer.querySelector('.bd-search-modal__panel[tabindex="-1"]');
    const searchInput = this.detailsContainer.querySelector('input:not([type="hidden"])');
    const closeButton = this.detailsContainer.querySelector(
      '.bd-search-modal__close-button, .bd-search-modal__title-dismiss'
    );
    const focusTarget =
      searchInput ||
      closeButton ||
      (panel &&
        panel.querySelector(
          'button:not([disabled]), [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )) ||
      panel ||
      this.summaryToggle;

    if (panel && focusTarget) {
      trapFocus(panel, focusTarget);
      if (this.isBdNutritionModal) {
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (e) {
          focusTarget.focus();
        }
      }
    }
  }

  close(focusToggle = true) {
    const returnFocusEl = focusToggle ? this.summaryToggle : null;

    if (this.isBdNutritionModal) {
      this._bdScrollY = window.scrollY || window.pageYOffset || 0;
      this.prepareNutritionModalClose();
    } else {
      removeTrapFocus(returnFocusEl);
    }

    const details = this.detailsContainer;
    const finishClose = () => {
      this._bdSearchDrawerClosing = false;

      if (this.isBdNutritionModal) {
        this.finishNutritionModalClose(details);
        return;
      }

      details.removeAttribute('open');
      details.classList.remove('bd-search-modal-opening');
      const sectionHeader = this.closest('.section-header');
      if (sectionHeader) sectionHeader.classList.remove('bd-search-modal-open');
      document.body.removeEventListener('click', this.onBodyClickEvent);

      this.unlockPageScroll();
    };

    if (!this.isOpen()) {
      const sectionHeader = this.closest('.section-header');
      if (sectionHeader) sectionHeader.classList.remove('bd-search-modal-open');
      document.body.removeEventListener('click', this.onBodyClickEvent);
      if (this.isBdNutritionModal) {
        this.unlockPageScroll();
        this.restoreNutritionScrollPosition();
      } else {
        this.unlockPageScroll();
      }
      return;
    }

    if (!this.usesBdSearchDrawerAnim) {
      finishClose();
      return;
    }

    if (this._bdSearchDrawerClosing) return;

    const reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !details.classList.contains('bd-search-modal-opening')) {
      finishClose();
      return;
    }

    this._bdSearchDrawerClosing = true;
    details.classList.remove('bd-search-modal-opening');

    const panel = details.querySelector('.bd-search-modal__panel');
    if (!panel) {
      finishClose();
      return;
    }

    let done = false;
    const runFinish = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    const onTransitionEnd = (e) => {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      runFinish();
    };

    panel.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(runFinish, 280);
  }
}

customElements.define('details-modal', DetailsModal);
